/**
 * Dock — public landing at `/`. Always accessible. Renders a "Sign in"
 * pill for unauthenticated visitors and an "Open dashboard" link for
 * already-signed-in users (no auto-redirect).
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Icon, Kbd, Logo, Pill } from '../components/brand';
import { useTweaks } from '../components/tweaks';

export function Dock() {
  const { loading, user, login } = useAuth();
  const navigate = useNavigate();
  const [tweaks, setTweak] = useTweaks();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (loading) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (user) navigate('/dashboard');
        else login();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [login, loading, user, navigate]);

  return (
    <div className="bm-dock">
      <div className="bm-dock-grid" aria-hidden />
      <div className="bm-dock-vignette" aria-hidden />

      <div
        className="bm-header-right"
        style={{ position: 'absolute', top: 20, right: 24, zIndex: 2 }}
      >
        <span className="bm-status-dot" title="connected" />
        <span className="mono" style={{ fontSize: 11, color: 'var(--bm-text-muted)' }}>
          CONNECTED
        </span>
        <button
          type="button"
          className="bm-btn bm-btn-icon"
          title={tweaks.theme === 'light' ? 'Switch to dark' : 'Switch to light'}
          onClick={() => setTweak('theme', tweaks.theme === 'light' ? 'dark' : 'light')}
        >
          {tweaks.theme === 'light' ? <Icon.moon size={15} /> : <Icon.sun size={15} />}
        </button>
      </div>

      <main className="bm-dock-panel">
        <div className="bm-dock-mark">
          <div className="bm-orbit-track" aria-hidden>
            <div className="bm-orbit-dot" />
          </div>
          <Logo size={72} alive />
        </div>

        <h1 className="bm-dock-title">bucket manager</h1>
        <p className="bm-dock-tagline">
          One console for every S3-compatible object store.
          <br />
          <span className="muted">
            Direct-to-storage transfers, OIDC sign-in, audit everything.
          </span>
        </p>

        <div className="bm-dock-cta">
          {user ? (
            <button
              type="button"
              className="bm-btn bm-btn-accent bm-btn-pill bm-btn-lg"
              onClick={() => navigate('/dashboard')}
            >
              <Icon.dashboard size={15} />
              <span>Open dashboard</span>
            </button>
          ) : (
            <button
              type="button"
              className="bm-btn bm-btn-accent bm-btn-pill bm-btn-lg"
              onClick={login}
              disabled={loading}
            >
              <Icon.signin size={15} />
              <span>{loading ? 'Checking session…' : 'Sign in'}</span>
            </button>
          )}
          <div className="bm-dock-hint">
            <span style={{ whiteSpace: 'nowrap' }}>or press</span>
            <Kbd>↩&nbsp;return</Kbd>
          </div>
        </div>

        <div className="bm-dock-chips">
          <Pill>
            <Icon.upload size={11} /> presigned direct transfers
          </Pill>
          <Pill>
            <Icon.lock size={11} /> OIDC SSO
          </Pill>
          <Pill>
            <Icon.globe size={11} /> R2 · S3 · MinIO · B2 · Wasabi
          </Pill>
        </div>
      </main>

      <footer className="bm-dock-footer mono">
        <span style={{ color: 'var(--bm-text-faint)' }}>CLOUDFLARE PAGES</span>
        <span style={{ color: 'var(--bm-text-ghost)' }}> · </span>
        <span style={{ color: 'var(--bm-text-faint)' }}>WORKERS</span>
        <span style={{ color: 'var(--bm-text-ghost)' }}> · </span>
        <span style={{ color: 'var(--bm-text-faint)' }}>D1</span>
        <span style={{ color: 'var(--bm-text-ghost)' }}> · </span>
        <span style={{ color: 'var(--bm-text-faint)' }}>R2</span>
      </footer>
    </div>
  );
}
